import { GatewayConformite, GatewayEntreprise } from "@conformite/gateway";
import {
  InfoCard,
  LoadingPage,
  Button,
  useDialog,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import ChevronLeftIcon from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import ChevronRightIcon from "@endya-conformite/ui-kit/src/assets/icons/chevron-right.svg?react";
import { AssujettiDetailConformite } from "@src/components/AssujettiDetailConformite/AssujettiDetailConformite";
import { SharedAssujettiAnswersProvider } from "@src/components/questionnaire/SharedAssujettiAnswersProvider";
import { useAssujettiListQuery } from "@src/data/assujetti/hook/useAssujettiListQuery";
import { useAnalyseEstimationQuery } from "@src/data/controle-quinquennal/hook/useAnalyseEstimationQuery";
import {
  ANALYSE_COST_BLOCKED_MESSAGE,
  getAnalyseCostBlockedMessage,
  isAnalyseCostBlockedError,
} from "@src/helper/analyseEstimation.helper";
import { useCQAxeVerificationsQuery } from "@src/data/controle-quinquennal/hook/useCQAxeVerificationsQuery";
import { useDeclarationActiviteIobspAdminQuery } from "@src/data/declaration-activite/hook/useDeclarationActiviteIobspAdminQuery";
import { useRelaunchSubAxisMutation } from "@src/data/controle-quinquennal/hook/useRelaunchSubAxisMutation";
import { getAvailableAxes } from "@src/helper/avancement.helper";
import { LISTE_ASSUJETTIS_SUBAXIS } from "@src/helper/subAxisVerification.helper";
import { useEffect, useMemo } from "react";
import { AnalyseEstimationMessage } from "../components/AnalyseEstimationMessage";
import styles from "./CQAxeAssujetti.module.scss";
import { ListeAssujettisCard } from "./ListeAssujettisCard";

type CQAxeAssujettiProps = {
  idCQ: string;
  idAxe: string;
  millesime: number;
  siren: string;
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType;
  selectedPersonneId?: string;
  onPersonneSelected: (personneId: string) => void;
  disabled?: boolean;
};

export function CQAxeAssujetti({
  idCQ,
  idAxe,
  millesime,
  siren,
  entreprise,
  selectedPersonneId,
  onPersonneSelected,
  disabled,
}: CQAxeAssujettiProps) {
  const { data: assujettis, isLoading } = useAssujettiListQuery(
    millesime,
    siren
  );

  // MR-G — vérifications IA par sous-axe. Map vide tant que le flag gateway
  // CQ_ASSUJETTI_AI !== 'on' : AUCUN élément d'UI supplémentaire n'est rendu
  // (page identique au pixel près à aujourd'hui).
  const { data: verificationsBySubAxis } = useCQAxeVerificationsQuery(
    idCQ,
    idAxe
  );
  const hasVerifications = (verificationsBySubAxis?.size ?? 0) > 0;

  const {
    mutate: relaunchSubAxis,
    isPending: isRelaunchingSubAxis,
    variables,
  } = useRelaunchSubAxisMutation();
  const relaunchingSubAxis = isRelaunchingSubAxis
    ? variables?.subAxis
    : undefined;

  const { openConfirmDialog } = useDialog();
  // rowCount/documentCount d'un run SOUS_AXE ne dépendent que de l'idAxe (cf.
  // ControleQuinquennalAnalyseEstimationService.resolvePlanCounts) : identiques
  // pour tout sous-axe de cet axe qui émet effectivement un message. Une seule
  // estimation, préchargée au montage, sert donc les relances LISTE et
  // personne × dimension de cette page.
  const { data: estimation } = useAnalyseEstimationQuery(idCQ, {
    scope: "SOUS_AXE",
    idAxe,
    subAxis: LISTE_ASSUJETTIS_SUBAXIS,
  });

  const handleRelaunchSubAxis = (subAxis: string) => {
    const costBlockedMessage = getAnalyseCostBlockedMessage(estimation);
    if (costBlockedMessage) {
      displayErrorToast(costBlockedMessage);
      return;
    }
    openConfirmDialog({
      title: "Relancer l'analyse de ce contrôle",
      message: (
        <AnalyseEstimationMessage
          message="Relancer l'analyse IA de ce contrôle ? Les réponses IA actuelles seront recalculées."
          estimation={estimation}
        />
      ),
      cancelText: "Annuler",
      confirmText: "Relancer",
    })
      .then((shouldRelaunch) => {
        if (!shouldRelaunch) return;
        relaunchSubAxis(
          { idControleQuinquennal: idCQ, idAxe, subAxis },
          {
            onSuccess: () => {
              displaySucessToast(
                "Analyse relancée pour ce contrôle. Le statut se met à jour automatiquement."
              );
            },
            onError: (error) => {
              if (isAnalyseCostBlockedError(error)) {
                displayErrorToast(ANALYSE_COST_BLOCKED_MESSAGE);
                return;
              }
              displayErrorToast(
                "Impossible de relancer l'analyse de ce contrôle."
              );
            },
          }
        );
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!selectedPersonneId && assujettis && assujettis.length > 0) {
      onPersonneSelected(assujettis[0].id);
    }
  }, [assujettis, selectedPersonneId, onPersonneSelected]);

  const assujettisLength = assujettis?.length ?? 0;
  const isValidAssujettis = assujettis && assujettisLength > 0;

  const availableAxes = useMemo(
    () => getAvailableAxes(entreprise),
    [entreprise]
  );

  // Diversification de l'activité déclarée (déclaration d'activité IOBSP) :
  // conditionne la coloration des justificatifs de capacité pro IOBSP.
  // undefined tant que la donnée n'est pas chargée → coloration par niveau seul.
  const hasCapaciteProIobspAxe = Boolean(
    availableAxes?.includes(GatewayConformite.AxeConformite.CAPACITE_PRO_IOBSP)
  );
  const {
    data: declarationActiviteIobsp,
    isSuccess: isDeclarationActiviteIobspLoaded,
  } = useDeclarationActiviteIobspAdminQuery(siren, millesime, {
    enabled: hasCapaciteProIobspAxe,
  });
  const declaredIobspActivites = useMemo(() => {
    if (!isDeclarationActiviteIobspLoaded) return undefined;
    if (!declarationActiviteIobsp) return [];
    return [
      ...(declarationActiviteIobsp.activitePrincipale
        ? [declarationActiviteIobsp.activitePrincipale]
        : []),
      ...declarationActiviteIobsp.activites10Pourcent,
    ];
  }, [isDeclarationActiviteIobspLoaded, declarationActiviteIobsp]);

  const selectedAssujettiIndex = useMemo(() => {
    if (!assujettis) return 0;
    const index = assujettis.findIndex((a) => a.id === selectedPersonneId);
    return index === -1 ? 0 : index;
  }, [selectedPersonneId, assujettis]);

  // §8.5 — lignes d'analyse dont la personne n'est plus dans la liste déclarée
  // (personne supprimée/masquée après émission) : affichées avec un libellé
  // neutre, jamais ignorées silencieusement.
  const orphanVerifications = useMemo(() => {
    if (!verificationsBySubAxis || !assujettis) return [];
    const knownIds = new Set(assujettis.map((assujetti) => assujetti.id));
    return [...verificationsBySubAxis.values()].filter(
      (verification) =>
        verification.subAxis !== LISTE_ASSUJETTIS_SUBAXIS &&
        verification.assujettiId &&
        !knownIds.has(verification.assujettiId)
    );
  }, [verificationsBySubAxis, assujettis]);

  if (isLoading) {
    return <LoadingPage withText={false} />;
  }

  if (!isValidAssujettis) {
    return (
      <div className={styles.emptyState}>
        <p>Aucune personne assujettie.</p>
      </div>
    );
  }

  const selectedAssujetti = assujettis[selectedAssujettiIndex];

  const navigateBetweenAssujettis = (offset: number) => {
    const newIndex =
      (selectedAssujettiIndex + offset + assujettisLength) % assujettisLength;
    const newPersonneId = assujettis[newIndex].id;
    onPersonneSelected(newPersonneId);
  };

  return (
    <SharedAssujettiAnswersProvider>
      <div>
        {hasVerifications && (
          <div className={styles.listeCard}>
            <ListeAssujettisCard
              idAxe={idAxe}
              verification={verificationsBySubAxis?.get(
                LISTE_ASSUJETTIS_SUBAXIS
              )}
              onRelaunch={
                disabled
                  ? undefined
                  : () => handleRelaunchSubAxis(LISTE_ASSUJETTIS_SUBAXIS)
              }
              isRelaunching={relaunchingSubAxis === LISTE_ASSUJETTIS_SUBAXIS}
              disabled={disabled}
            />
          </div>
        )}
        <div className={styles.container}>
          <div className={styles.content}>
            {selectedAssujetti && (
              <AssujettiDetailConformite
                assujetti={selectedAssujetti}
                availableAxes={availableAxes}
                verificationsBySubAxis={verificationsBySubAxis}
                onRelaunchSubAxis={disabled ? undefined : handleRelaunchSubAxis}
                relaunchingSubAxis={relaunchingSubAxis}
                declaredIobspActivites={declaredIobspActivites}
                disabled={disabled}
              />
            )}
          </div>
          {assujettisLength > 1 && (
            <div className={styles.navigation}>
              {selectedAssujettiIndex > 0 && (
                <Button
                  className={styles.previousButton}
                  onClick={() => navigateBetweenAssujettis(-1)}
                  icon={<ChevronLeftIcon className={styles.chevron} />}
                >
                  Personne précédente
                </Button>
              )}
              {selectedAssujettiIndex < assujettisLength - 1 && (
                <Button
                  className={styles.nextButton}
                  onClick={() => navigateBetweenAssujettis(1)}
                  iconVariant="right"
                  icon={<ChevronRightIcon className={styles.chevron} />}
                >
                  Personne suivante
                </Button>
              )}
            </div>
          )}
        </div>
        {orphanVerifications.length > 0 && (
          <InfoCard type="neutral" className={styles.orphanCard}>
            <div>
              <p className={styles.orphanTitle}>
                Analyses IA de personnes retirées de la liste déclarée :
              </p>
              <ul className={styles.orphanList}>
                {orphanVerifications.map((verification) => (
                  <li key={verification.subAxis}>
                    {verification.resultPayload?.sujet?.displayName ??
                      `Personne ${verification.assujettiId}`}
                    {" — "}
                    {verification.dimension ?? verification.subAxis} (
                    {verification.status})
                  </li>
                ))}
              </ul>
            </div>
          </InfoCard>
        )}
      </div>
    </SharedAssujettiAnswersProvider>
  );
}
